// ============================================================================
// JARVISTRAVEL - CONSTANTS
// Version: 1.0.0
// ============================================================================

import type {
  LoyaltyProgram,
  BudgetCategory,
  FatigueLevel,
} from '@/app/types';

// Shared ceiling for the per-session photo caches (resolved answers, trip
// sights, and the negative/failure cache beside them). One value so the three
// maps cannot drift apart (JAR-3708 review, LOW 6).
export const PHOTO_CACHE_MAX_ENTRIES = 300;

// -----------------------------------------------------------------------------
// APP CONFIGURATION
// -----------------------------------------------------------------------------

export const APP_CONFIG = {
  name: 'JarvisTravel',
  tagline: 'Your Personal Travel Assistant',
  version: '1.0.0',
  maxCollaborators: 10,
  maxProperties: 4,
  defaultCurrency: 'USD',
  defaultLanguage: 'en',
} as const;

// -----------------------------------------------------------------------------
// MARKETING SITE
// -----------------------------------------------------------------------------

/**
 * The marketing site's origin, the one place the app's links to it are built
 * from. The site is the apex; www redirects to it.
 *
 * A constant, not a VITE_ variable: no build should vary it. The terms a user
 * agreed to are the ones on the real site, so a build pointing elsewhere would
 * show them the wrong document. A wrong constant fails a test; an unset env var
 * falls back and ships.
 */
export const MARKETING_ORIGIN = 'https://jarvistravel.com';

/**
 * Each path renders its own document. Check one by what it renders, not by its
 * status: the site answers 200 for any path, and renders its home page for a
 * path it doesn't have.
 *
 * The tests that pin these URLs spell them out as literal strings on purpose. A
 * test that builds its expectation from the constant it is checking passes
 * whatever the constant says.
 */
export const MARKETING_URLS = {
  home: `${MARKETING_ORIGIN}/`,
  terms: `${MARKETING_ORIGIN}/terms`,
  privacy: `${MARKETING_ORIGIN}/privacy`,
  cookies: `${MARKETING_ORIGIN}/cookies`,
} as const;

/**
 * The support mailbox the app actually shows.
 *
 * APP_CONFIG carried `supportEmail: 'support@jarvistravel.com'` — a DIFFERENT
 * address, used by nothing, sitting in the file a reader would check first.
 * Removed rather than reconciled: `help@` is the one on screen and the one
 * SettingsTab.test.tsx pins, so `support@` was not a second opinion, it was a
 * wrong answer with better placement.
 */
export const SUPPORT_EMAIL = 'help@jarvistravel.com';

/** The public status page, shown in Help & Support (JAR-2647). */
export const STATUS_URL = 'https://status.jarvistravel.com/';

// -----------------------------------------------------------------------------
// ROUTE CONSTANTS
// -----------------------------------------------------------------------------

export const ROUTES = {
  LOADER: 'loader',
  AUTH: 'auth',
  ONBOARDING: 'onboarding',
  DASHBOARD: 'dashboard',
} as const;

export const AUTH_ROUTES = {
  SIGNIN: 'signin',
  SIGNUP: 'signup',
  FORGOT_PASSWORD: 'forgot-password',
  VERIFY_CODE: 'verify-code',
  RESET_PASSWORD: 'reset-password',
} as const;

// The public, read-only shared journal (J4). One definition for the route, for
// the URL every share link is built from, and for the gate that lets a signed-in
// visitor stay on a share page. The trip Share modal once built /share/{token},
// a path no route served, and nothing noticed (JAR-1543).
export const SHARED_JOURNAL_PREFIX = '/j/';
export const SHARED_JOURNAL_ROUTE = `${SHARED_JOURNAL_PREFIX}:token`;

export const DASHBOARD_TABS = {
  HOME: 'home',
  EXPLORE: 'explore',
  ITINERARY: 'itinerary',
  PROPERTY: 'property',
  JOURNAL: 'journal',
  SETTINGS: 'settings',
} as const;

// -----------------------------------------------------------------------------
// ONBOARDING STEPS
// -----------------------------------------------------------------------------

export const ONBOARDING_STEPS = [
  { id: 'welcome', title: 'Welcome', description: "Let's get to know you" },
  { id: 'loyalty', title: 'Loyalty Programs', description: 'Connect your travel programs' },
  { id: 'property', title: 'Where are you staying?', description: 'Select your properties' },
  { id: 'dates', title: 'Travel Dates', description: 'When are you traveling?' },
  { id: 'travelers', title: 'Travelers', description: 'Who is joining?' },
  { id: 'budget', title: 'Budget', description: 'Set your spending limits' },
  { id: 'complete', title: 'All Set!', description: 'Meet your AI assistant' },
] as const;

// -----------------------------------------------------------------------------
// LOYALTY PROGRAMS
// -----------------------------------------------------------------------------

export const LOYALTY_PROGRAMS: LoyaltyProgram[] = [
  { id: 'marriott', name: 'Marriott Bonvoy', type: 'hotel', icon: '🏨', connected: false },
  { id: 'hilton', name: 'Hilton Honors', type: 'hotel', icon: '🏨', connected: false },
  { id: 'ihg', name: 'IHG One Rewards', type: 'hotel', icon: '🏨', connected: false },
  { id: 'hyatt', name: 'World of Hyatt', type: 'hotel', icon: '🏨', connected: false },
  { id: 'wyndham', name: 'Wyndham Rewards', type: 'hotel', icon: '🏨', connected: false },
  { id: 'aa', name: 'American Airlines', type: 'airline', icon: '✈️', connected: false },
  { id: 'delta', name: 'Delta SkyMiles', type: 'airline', icon: '✈️', connected: false },
  { id: 'united', name: 'United MileagePlus', type: 'airline', icon: '✈️', connected: false },
  { id: 'southwest', name: 'Southwest Rapid Rewards', type: 'airline', icon: '✈️', connected: false },
  { id: 'amex', name: 'Amex Membership Rewards', type: 'card', icon: '💳', connected: false },
  { id: 'chase', name: 'Chase Ultimate Rewards', type: 'card', icon: '💳', connected: false },
  { id: 'citi', name: 'Citi ThankYou', type: 'card', icon: '💳', connected: false },
];

// -----------------------------------------------------------------------------
// BUDGET CATEGORIES
// -----------------------------------------------------------------------------

export const DEFAULT_BUDGET_CATEGORIES: BudgetCategory[] = [
  // Meridian data set: FI-chill sky for air, Ateneo mid for ground transit,
  // dusk mauve (from the brand gradient) for stays, success green for food,
  // deepened amber for doing, deep coral for shopping, muted for the rest.
  // Red is reserved for errors and never used as a category.
  { id: 'flights', name: 'Flights', allocated: 0, spent: 0, icon: '✈️', color: '#0EA5E9' },
  { id: 'hotels', name: 'Hotels', allocated: 0, spent: 0, icon: '🏨', color: '#6E4A62' },
  { id: 'food', name: 'Food & Dining', allocated: 0, spent: 0, icon: '🍽️', color: '#059669' },
  { id: 'activities', name: 'Activities', allocated: 0, spent: 0, icon: '🎭', color: '#C98A2E' },
  { id: 'transport', name: 'Transport', allocated: 0, spent: 0, icon: '🚗', color: '#2E6FA3' },
  { id: 'shopping', name: 'Shopping', allocated: 0, spent: 0, icon: '🛍️', color: '#B23F2C' },
  { id: 'other', name: 'Other', allocated: 0, spent: 0, icon: '📦', color: '#5C6B77' },
];

// ACTIVITY_TYPE_CONFIG and EXPLORE_CATEGORIES were removed (JAR-436): they were
// unreferenced dead constants whose only content was emoji, which the no-emoji
// UI rule bans. The live screens already render lucide icons from their own
// maps — ACTIVITY_ICON in ItineraryTab and CATEGORY_ICON in ExploreTab.

// -----------------------------------------------------------------------------
// FATIGUE INDEX
// -----------------------------------------------------------------------------

// Public FI is a 1–9 scale: 1–3 Chill · 4–6 Balanced · 7–9 Packed. Colors follow
// the fatigue scale (chill blue → balanced green → packed clay-red); UI components
// should prefer the token classes in utils/fi.ts over these raw hexes.
export const FATIGUE_LEVELS: Record<FatigueLevel, { min: number; max: number; color: string; description: string }> = {
  Chill: {
    min: 1,
    max: 3,
    color: '#0EA5E9',
    description: 'Relaxed pace with plenty of rest time'
  },
  Balanced: {
    min: 4,
    max: 6,
    color: '#059669',
    description: 'Balanced mix of activities and downtime'
  },
  Packed: {
    min: 7,
    max: 9,
    color: '#D35446',
    description: 'Intensive schedule. Consider lightening the load'
  },
};

export const FATIGUE_WEIGHTS = {
  travelTime: 0.25,
  activityDensity: 0.30,
  restPeriods: 0.20,
  jetLag: 0.15,
  walkingDistance: 0.10,
} as const;

// -----------------------------------------------------------------------------
// THEME COLORS
// -----------------------------------------------------------------------------

export const THEME = {
  colors: {
    primary: {
      50: '#EDF4FA',
      100: '#DBE9F4',
      200: '#B7D3E9',
      300: '#8CC0E8',
      400: '#4E8FC7',
      500: '#2E6FA3',
      600: '#003A6C',
      700: '#083258',
      800: '#0B2B49',
      900: '#0E2A47',
    },
    secondary: {
      50: '#F6F1F4',
      100: '#EBDFE7',
      200: '#D6BFCF',
      300: '#BC9AB1',
      400: '#A85D79',
      500: '#8A5474',
      600: '#6E4A62',
      700: '#5A3C50',
      800: '#452E3E',
      900: '#33222E',
    },
    success: '#059669',
    warning: '#9A5B00',
    error: '#BE123C',
    info: '#003A6C',
    gray: {
      50: '#F0EEEB',
      100: '#E8E4DE',
      200: '#DAD5CD',
      300: '#C7C1B8',
      400: '#8E9AA4',
      500: '#5C6B77',
      600: '#4A5560',
      700: '#3A444C',
      800: '#262E33',
      900: '#13181B',
    },
  },
  // Content presets only (e.g. cover fallbacks) — Meridian bans gradients in
  // chrome, and each of these stays tonal within one family per the guide.
  gradients: {
    primary: 'linear-gradient(135deg, #003A6C 0%, #2E6FA3 100%)',
    secondary: 'linear-gradient(135deg, #6E4A62 0%, #A85D79 100%)',
    success: 'linear-gradient(135deg, #059669 0%, #34D399 100%)',
    sunset: 'linear-gradient(135deg, #B23F2C 0%, #FFBF65 100%)',
    ocean: 'linear-gradient(135deg, #0E2A47 0%, #2E6FA3 100%)',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    '2xl': '1.5rem',
    full: '9999px',
  },
} as const;

// -----------------------------------------------------------------------------
// VALIDATION
// -----------------------------------------------------------------------------

export const VALIDATION = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  name: {
    minLength: 2,
    maxLength: 50,
  },
} as const;

// -----------------------------------------------------------------------------
// ANIMATION TIMINGS
// -----------------------------------------------------------------------------

export const ANIMATIONS = {
  fast: 150,
  normal: 300,
  slow: 500,
  loader: 2500,
} as const;

// -----------------------------------------------------------------------------
// LOCAL STORAGE KEYS
// -----------------------------------------------------------------------------

export const STORAGE_KEYS = {
  user: 'jarvis_user',
  // No `token`. Auth moved to Clerk, which mints per-request and never
  // persists; the key was dead but its presence invited someone to start
  // writing a JWT to localStorage again. Legacy values left over from the
  // pre-Clerk era are swept at boot — see purgeLegacyAuthKeys (JAR-355, low).

  onboarding: 'jarvis_onboarding',
  theme: 'jarvis_theme',
  recentSearches: 'jarvis_recent_searches',
} as const;

/** The most of what someone tells the planner that core keeps on a trip, in
 *  characters (JAR-1874). The planner's boxes and Edit trip stop there. */
export const MAX_PLANNER_NOTES = 1000;

/** The most the planner's place box takes, in characters: room for a place
 *  name, and a stop for a paragraph pasted into it (JAR-2016). */
export const MAX_PLANNER_DESTINATION = 80;
